/**
 * The receivables position, drawn (docs/tally/18 §4–§6).
 *
 * Owed, held and net always together; how the net is made up, line by line;
 * and the ageing as TallyPrime's own Age-wise report shows it — the debit
 * bills, by the company's buckets — with credits and money in no bill listed
 * apart, each with its reason. Nothing here is computed: the domain did it
 * (`interpretReceivables`, `previewBuckets`), and this only lays it out.
 */
import { AgeTag, ProportionBar, Table, Td, Th, RowHead, cx } from '@timely/ui';
import { ageingWindowRows, windowsBalance, type AgeingWindows, type BucketPreview, type PositionSummary } from '@timely/domain';
import { useFormatters } from '../book/useFormatters';

/** Owed to you · Held for customers · Net receivable — one set of figures, never one without the others. */
export function PositionFigures({ position }: { position: PositionSummary }) {
    const { money, count } = useFormatters();
    const net = position.net;
    const items: [string, string, string][] = [
        ['Owed to you', money.exact(position.owedToYou), `${count(position.accounts.inDebit)} account${position.accounts.inDebit === 1 ? '' : 's'} in debit`],
        ['Held for customers', money.exact(position.heldForCustomers), `${count(position.accounts.inCredit)} account${position.accounts.inCredit === 1 ? '' : 's'} in credit`],
        ['Net receivable', money.balance(Math.abs(net), net < 0 ? 'Cr' : 'Dr'), 'TallyPrime’s Trial Balance shows the same for Sundry Debtors'],
    ];
    return (
        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-3" aria-label="Owed, held and net">
            {items.map(([label, value, note]) => (
                <div key={label} className="min-w-0">
                    <dt className="text-caption text-label-3">{label}</dt>
                    <dd className="mt-1 text-heading font-semibold text-label num">{value}</dd>
                    <dd className="mt-0.5 text-caption text-label-3">{note}</dd>
                </div>
            ))}
        </dl>
    );
}

/** How the net is made up: open bills, money in no bill, credits — each from TallyPrime, each with its reason. */
export function CompositionTable({ position }: { position: PositionSummary }) {
    const { money } = useFormatters();
    const c = position.composition;
    const rows: [string, string, number, 'plus' | 'minus'][] = [
        ['Open bills', 'What customers owe, bill by bill', c.debitBills, 'plus'],
        ['Kept without bills', 'Ledgers TallyPrime keeps without bill-by-bill details', c.debitNotBillWise, 'plus'],
        ['Owed with no bill', 'In the balance but in no bill — often an opening balance', c.debitOnAccount, 'plus'],
        ['Credit notes and advances', 'Held for customers, against no bill yet', c.creditBills, 'minus'],
        ['Received On Account', 'Received against no particular bill', c.creditOnAccount, 'minus'],
        ['Credit kept without bills', 'Credit on ledgers kept without bills', c.creditNotBillWise, 'minus'],
    ];
    return (
        <Table caption="How the net receivable is made up">
            <thead><tr><Th>How the net is made up</Th><Th numeric>Amount</Th></tr></thead>
            <tbody>
                {rows.filter(r => r[2] !== 0 || r[0] === 'Open bills').map(([label, hint, amount, sign]) => (
                    <tr key={label}>
                        <RowHead className="font-normal text-label-2">
                            <span className="block text-label">{label}</span>
                            <span className="block text-caption text-label-3 font-normal">{hint}</span>
                        </RowHead>
                        <Td numeric>{sign === 'minus' ? `− ${money.exact(amount)}` : money.exact(amount)}</Td>
                    </tr>
                ))}
                <tr>
                    <RowHead>Net receivable</RowHead>
                    <Td numeric className="font-semibold text-label">{money.balance(Math.abs(position.net), position.net < 0 ? 'Cr' : 'Dr')}</Td>
                </tr>
            </tbody>
        </Table>
    );
}

/**
 * The ageing of the open bills, by the company's buckets, with what is in no
 * bucket said in words. `preview` comes from the histogram (any layout, before
 * saving); the position's own ageing is the same shape.
 */
export function AgeingTable({ preview, position, caption }: { preview: BucketPreview; position: PositionSummary; caption: string }) {
    const { money, count } = useFormatters();
    const total = preview.total.amount;
    const share = (amount: number) => (total > 0 ? amount / total : 0);
    const notInBuckets: [string, string, number][] = [
        ...(preview.basis === 'due_date' ? [['Not yet due', 'Due on or after the date the figures are stated at', preview.notYetDue.amount] as [string, string, number]] : []),
        ...(preview.unagedNoDueDate.bills ? [['No due date from TallyPrime', 'Left out of the buckets, by your settings', preview.unagedNoDueDate.amount] as [string, string, number]] : []),
    ];
    return (
        <div>
            <Table caption={caption}>
                <thead>
                    <tr>
                        <Th>{preview.basis === 'due_date' ? 'Days past the due date' : 'Days since the bill date'}</Th>
                        <Th numeric hideBelow="sm">Bills</Th>
                        <Th numeric>Amount</Th>
                        <Th hideBelow="md" className="w-40"><span className="sr-only">Share</span></Th>
                    </tr>
                </thead>
                <tbody>
                    {preview.buckets.map(b => (
                        <tr key={b.label}>
                            <RowHead className="font-normal text-label-2">{b.label}</RowHead>
                            <Td numeric hideBelow="sm" className="text-label-3">{count(b.bills)}</Td>
                            <Td numeric>{money.exact(b.amount)}</Td>
                            <Td hideBelow="md"><ProportionBar share={share(b.amount)} label={`${b.label}: ${Math.round(share(b.amount) * 100)}% of the open bills`} /></Td>
                        </tr>
                    ))}
                    {notInBuckets.map(([label, hint, amount]) => (
                        <tr key={label}>
                            <RowHead className="font-normal text-label-2">
                                <span className="block">{label}</span>
                                <span className="block text-caption text-label-3">{hint}</span>
                            </RowHead>
                            <Td numeric hideBelow="sm" />
                            <Td numeric>{money.exact(amount)}</Td>
                            <Td hideBelow="md"><ProportionBar share={share(amount)} label={`${label}: ${Math.round(share(amount) * 100)}% of the open bills`} /></Td>
                        </tr>
                    ))}
                    <tr>
                        <RowHead>Open bills</RowHead>
                        <Td numeric hideBelow="sm" className="text-label-3">{count(preview.total.bills)}</Td>
                        <Td numeric className="font-semibold text-label">{money.exact(total)}</Td>
                        <Td hideBelow="md" />
                    </tr>
                </tbody>
            </Table>
            <NotAged position={position} />
        </div>
    );
}

/** What no bucket holds, by TallyPrime's reason. Never put in a bucket; never left out of the net. */
export function NotAged({ position }: { position: PositionSummary }) {
    const { money } = useFormatters();
    const c = position.composition;
    const lines: [string, number, 'Dr' | 'Cr'][] = [
        ['Kept without bills in TallyPrime', c.debitNotBillWise, 'Dr'],
        ['Owed with no bill', c.debitOnAccount, 'Dr'],
        ['Credit notes and advances, against no bill yet', c.creditBills, 'Cr'],
        ['Received On Account, against no bill', c.creditOnAccount, 'Cr'],
        ['Credit on ledgers kept without bills', c.creditNotBillWise, 'Cr'],
    ];
    const shown = lines.filter(l => l[1] !== 0);
    if (!shown.length) return <p className="mt-3 text-secondary text-label-3 px-5">Every amount is in an open bill: nothing is left unaged.</p>;
    return (
        <div className="mt-4">
            <h4 className="px-5 text-secondary font-semibold text-label">Not in any bucket</h4>
            <p className="px-5 text-caption text-label-3">TallyPrime holds these against no bill, so Timely does not age them or guess where they belong. They are in the net.</p>
            <ul className="mt-2 divide-y divide-separator border-t border-separator">
                {shown.map(([label, amount, side]) => (
                    <li key={label} className="flex items-center justify-between gap-4 px-5 py-2 text-body">
                        <span className="text-label-2">{label}</span>
                        {/* A credit reduces the net: shown with a minus, as in "How the net is made up" — not as an excess payment. */}
                        <span className={cx('num whitespace-nowrap', side === 'Cr' ? 'text-label-2' : 'text-label')}>{side === 'Cr' ? `− ${money.exact(amount)}` : money.exact(amount)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * One TallyPrime account's ageing in its company's own windows, named as
 * Settings names them (docs/tally/18 §1.3) — the same figures Reports and
 * Today add up. What the windows leave out is said beside them, never inside:
 * windows + not yet due + not in any bucket − credits = the account's balance.
 *
 * `strip` lays the windows across the account's statement, each under the
 * colour the ageing bar gives it, to the paisa; the statement states the
 * credits and the balance itself, so the strip says only what is owed and how
 * old it is.
 */
export function AccountWindows({ windows, layout = 'list', className }: { windows: AgeingWindows; layout?: 'list' | 'strip'; className?: string }) {
    const { money } = useFormatters();
    const balance = windowsBalance(windows);
    const beside: [string, number, 'Dr' | 'Cr'][] = [
        ...(windows.basis === 'due_date' || windows.notYetDue > 0 ? [['Not yet due', windows.notYetDue, 'Dr'] as [string, number, 'Dr']] : []),
        ...(windows.notAged > 0 ? [['Not in any bucket', windows.notAged, 'Dr'] as [string, number, 'Dr']] : []),
        ...(windows.credits > 0 ? [['Credits, against no bill yet', windows.credits, 'Cr'] as [string, number, 'Cr']] : []),
    ];
    if (layout === 'strip') {
        const rows = ageingWindowRows(windows);
        return (
            <dl aria-label="How old the money is, in your windows"
                className={cx('grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-x-6 gap-y-3', className)}>
                {rows.map(({ label, amount }, i) => (
                    <div key={label} className="min-w-0">
                        <dt><AgeTag index={i} of={rows.length}>{label}</AgeTag></dt>
                        <dd className={cx('mt-0.5 text-secondary font-semibold num', amount > 0 ? 'text-label' : 'text-label-3')}>{money.precise(amount)}</dd>
                    </div>
                ))}
                {beside.filter(([, , side]) => side === 'Dr').map(([label, amount]) => (
                    <div key={label} className="min-w-0">
                        <dt className="text-secondary text-label-3 whitespace-nowrap">{label}</dt>
                        <dd className="mt-0.5 text-secondary num text-label-2">{money.precise(amount)}</dd>
                    </div>
                ))}
            </dl>
        );
    }
    return (
        <dl aria-label="How old the money is, in your windows">
            {ageingWindowRows(windows).map(({ label, amount }) => (
                <div key={label} className="flex items-baseline justify-between gap-3 py-1">
                    <dt className="text-secondary text-label-3">{label}</dt>
                    <dd className={cx('text-secondary font-semibold num', amount > 0 ? 'text-label' : 'text-label-3')}>{money.exact(amount)}</dd>
                </div>
            ))}
            {beside.map(([label, amount, side]) => (
                <div key={label} className="flex items-baseline justify-between gap-3 py-1 border-t border-separator first-of-type:border-t-0">
                    <dt className="text-secondary text-label-3">{label}</dt>
                    <dd className="text-secondary num text-label-2">{side === 'Cr' ? `− ${money.exact(amount)}` : money.exact(amount)}</dd>
                </div>
            ))}
            {beside.length > 0 && (
                <div className="flex items-baseline justify-between gap-3 py-1 border-t border-separator-strong">
                    <dt className="text-secondary font-semibold text-label">Balance in TallyPrime</dt>
                    <dd className="text-secondary font-semibold num text-label">{money.balance(Math.abs(balance), balance < 0 ? 'Cr' : 'Dr')}</dd>
                </div>
            )}
        </dl>
    );
}
